import type { StackProps } from '@mui/material'
import { Checkbox, FormControlLabel, Stack } from '@mui/material'
import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { ConfigField } from '@/features/controller/configuration/components/drawers/ConfigField'
import { IntegerConfigField } from '@/features/controller/configuration/components/drawers/IntegerConfigField'
import { ToggleButtonGroupEnumConfigField } from '@/features/controller/configuration/components/drawers/ToggleButtonGroupEnumConfigField'
import type { ModbusRegister } from '@/features/controller/configuration/models/raspiProperties/types'
import { ModbusRegisterType } from '@/features/controller/configuration/models/raspiProperties/types'

type PartialRegister = Partial<ModbusRegister>

type RegisterConfigFieldProps = Omit<StackProps, 'flexDirection' | 'gap'> & {
  addressLabel: string
  checkboxLabel: string
  dataType?: string
  name: string
}

export function RegisterConfigField({
  addressLabel,
  checkboxLabel,
  dataType,
  name,
  ...stackProps
}: Readonly<RegisterConfigFieldProps>) {
  const { t } = useTranslation()
  const { watch, setValue } = useFormContext()
  const [previousValue, setPreviousValue] = useState<PartialRegister>({})

  const registerObject = watch(name)
  const isRegisterEnabled = registerObject !== undefined

  return (
    <Stack {...stackProps} flexDirection="column">
      <FormControlLabel
        control={
          <Checkbox
            checked={isRegisterEnabled}
            onChange={(event) => {
              const enabled = event.target.checked
              if (!enabled) {
                // Remember the previous value for the case when the user decides to enable it again
                setPreviousValue(registerObject)
              }
              setValue(name, enabled ? previousValue : undefined)
            }}
          />
        }
        label={checkboxLabel}
      />
      {isRegisterEnabled && (
        <Stack flexDirection="row">
          <IntegerConfigField
            flex={3}
            label={addressLabel}
            name={`${name}.address`}
            textFieldProps={{
              slotProps: { htmlInput: { min: 0 } },
            }}
          />
          <ToggleButtonGroupEnumConfigField
            enumLabels={{
              holding: t('controllers.configuration.power_meters.modbus.dialog.register_field.register_type.holding'),
              input: t('controllers.configuration.power_meters.modbus.dialog.register_field.register_type.input'),
            }}
            enumType={ModbusRegisterType}
            flex={2}
            label={t('controllers.configuration.power_meters.modbus.dialog.register_field.registers')}
            name={`${name}.registers`}
            toggleButtonGroupControllerProps={{ sx: { height: '42px' } }}
          />
          {dataType && (
            <ConfigField
              flex={1}
              label={t('controllers.configuration.power_meters.modbus.dialog.register_field.data_type')}
            >
              <CustomTypography alignContent="center" height={42}>
                {dataType}
              </CustomTypography>
            </ConfigField>
          )}
        </Stack>
      )}
    </Stack>
  )
}
